/**
 * Perfil de mapeamento de atributos de marketplace.
 *
 * O problema concreto: hoje existem cinco componentes que fazem a MESMA coisa —
 * renderizar a lista de atributos de uma categoria e gravar o que o usuário
 * escolheu — e nenhum deles reaproveita nada do outro:
 *
 *   shopee/shopee-attribute        219 l
 *   b2w/b2w-attribute              276 l
 *   tiktok/tiktok-attribute        369 l
 *   viavarejo/viavarejo-attribute  301 l
 *   mercadolivre/meli-item-attrib  404 l
 *
 * São ~1.570 linhas resolvendo o mesmo problema cinco vezes, porque o JSON de
 * origem de cada marketplace tem nome e forma diferentes para os mesmos conceitos.
 *
 * A observação que destrava: as diferenças são todas DECLARÁVEIS. Nenhuma exige
 * lógica nova — só dizer onde está cada coisa. Levantamento sobre os cinco:
 *
 * | Conceito     | Shopee                    | TikTok                  | B2W                     | Meli          |
 * |--------------|---------------------------|-------------------------|-------------------------|---------------|
 * | lista        | response.list[0].attribute_tree | data.attributes   | CATEGORY.attributes     | raiz          |
 * | id           | attribute_id              | id                      | nameId                  | id            |
 * | rótulo       | name (+ multi_lang[])     | name                    | name                    | name          |
 * | obrigatório  | mandatory                 | is_required             | type/regra              | tags          |
 * | opções       | attribute_value_list[]    | values[]                | dadosValores[]          | values[]      |
 * | valor da opção | value_id                | id                      | id                      | id            |
 * | rótulo da opção | name                   | name                    | name                    | name          |
 * | múltiplo     | max_value_count > 1       | is_multiple_selection   | type == 'Radio'         | value_type    |
 * | texto livre  | input_type                | is_customizable         | —                       | value_type    |
 * | unidade      | attribute_unit_list[]     | —                       | —                       | —             |
 * | aninhamento  | ÁRVORE (child_attribute_list sob o valor) | plano   | agrupado (grupId)       | hierárquico   |
 *
 * Com este perfil, criar a tela de atributos de um marketplace novo passa a ser
 * arrastar um componente e preencher o mapeamento — sem componente Angular novo.
 */

/**
 * Como a lista de atributos se organiza.
 *
 * `flat`     — lista simples. TikTok.
 * `grouped`  — lista simples, agrupada por um campo. B2W (`grupId`), ViaVarejo.
 * `tree`     — atributo filho pendurado num VALOR do atributo pai. Shopee
 *              (`child_attribute_list` dentro de `attribute_value_list`), e é o
 *              caso que exige treeview de verdade: a escolha do pai muda quais
 *              filhos aparecem.
 */
export type AttributeNesting = 'flat' | 'grouped' | 'tree';

/**
 * Onde cada conceito está no JSON de origem.
 *
 * Todo campo é um caminho relativo ao objeto do atributo, com ponto para
 * aninhamento. O que não existir no marketplace fica ausente, e o renderer usa o
 * padrão — não é erro.
 */
export interface AttributeFieldMap {
  /** Identificador do atributo. Ex.: 'attribute_id', 'id', 'nameId'. */
  readonly id: string;
  /** Rótulo exibido. Ex.: 'name'. */
  readonly label: string;

  /** Campo booleano de obrigatoriedade. Ex.: 'mandatory', 'is_required'. */
  readonly required?: string;
  /** Campo com a lista de opções. Ex.: 'attribute_value_list', 'values'. */
  readonly options?: string;
  /** Dentro de uma opção: o valor gravado. Ex.: 'value_id', 'id'. */
  readonly optionValue?: string;
  /** Dentro de uma opção: o texto exibido. Ex.: 'name'. */
  readonly optionLabel?: string;

  /** Campo que indica seleção múltipla. Ex.: 'is_multiple_selection'. */
  readonly multiple?: string;
  /**
   * Campo que indica se o usuário pode digitar valor fora da lista.
   * Ex.: 'is_customizable' (TikTok), 'input_type' (Shopee).
   */
  readonly allowFreeText?: string;
  /** Campo com a lista de unidades. Ex.: 'attribute_unit_list' (Shopee). */
  readonly units?: string;

  /** Campo de agrupamento, quando `nesting: 'grouped'`. Ex.: 'grupId'. */
  readonly group?: string;
  /**
   * Campo com os atributos filhos, quando `nesting: 'tree'`.
   * No Shopee os filhos ficam DENTRO da opção, não do atributo — por isso este
   * caminho é resolvido a partir da opção.
   */
  readonly children?: string;

  /** Rótulos por idioma, quando o marketplace manda. Ex.: 'multi_lang'. */
  readonly translations?: string;
  /** Dentro de uma tradução: o idioma e o texto. */
  readonly translationLanguage?: string;
  readonly translationText?: string;
}

/**
 * Como gravar o que o usuário escolheu.
 *
 * Cada marketplace grava em tabela e colunas próprias — Shopee usa
 * ATTRIBUTE_ID/DATAVALUE/UNITVALUE, TikTok usa KEY/VALUE/OPTIONS, B2W usa
 * KEY_ESPECIFICATION/VALUE_ESPECIFICATION. O nome muda; a estrutura, não.
 */
export interface AttributePersistence {
  /** Entidade onde as escolhas são gravadas. */
  readonly entityName: string;
  /** Coluna que guarda o id do atributo. Ex.: 'ATTRIBUTE_ID', 'KEY'. */
  readonly idColumn: string;
  /** Coluna que guarda o valor escolhido. Ex.: 'DATAVALUE', 'VALUE'. */
  readonly valueColumn: string;
  /** Coluna da unidade, quando o marketplace tem. Ex.: 'UNITVALUE'. */
  readonly unitColumn?: string;
  /** Coluna que guarda as opções resolvidas. Ex.: 'OPTIONS'. */
  readonly optionsColumn?: string;
  /**
   * Colunas de chave do registro pai, copiadas do contexto da tela.
   * Ex.: ['CODECOMPANY', 'CODEPRODUCT', 'CODECONTRACT'] — hoje cada componente
   * lê isso à mão de `data.parentRow.linha`.
   */
  readonly keyColumns: readonly string[];
}

/** O perfil completo de um marketplace. */
export interface AttributeProfile {
  /** Identificador do perfil. Ex.: 'shopee', 'tiktok'. */
  readonly id: string;
  /** Chave de tradução do nome exibido. */
  readonly labelKey: string;

  /** Caminho, no JSON de origem, até o array de atributos. */
  readonly sourcePath: string;
  readonly nesting: AttributeNesting;
  readonly fields: AttributeFieldMap;
  readonly persistence?: AttributePersistence;
}

/**
 * Perfis nativos, prontos para uso.
 *
 * São dados, não código: um marketplace novo entra como mais uma entrada aqui,
 * ou é montado pelo próprio usuário no inspector. Os caminhos vieram da leitura
 * dos cinco componentes existentes.
 */
export const SHOPEE_ATTRIBUTE_PROFILE: AttributeProfile = {
  id: 'shopee',
  labelKey: 'builderScreen.attributes.profile.shopee',
  sourcePath: 'response.list[0].attribute_tree',
  nesting: 'tree',
  fields: {
    id: 'attribute_id',
    label: 'name',
    required: 'mandatory',
    options: 'attribute_value_list',
    optionValue: 'value_id',
    optionLabel: 'name',
    allowFreeText: 'attribute_info.input_type',
    units: 'attribute_info.attribute_unit_list',
    // No Shopee o filho pende do VALOR, não do atributo — é o que faz a escolha
    // do pai mudar quais filhos aparecem.
    children: 'child_attribute_list',
    translations: 'multi_lang',
    translationLanguage: 'language',
    translationText: 'value'
  },
  persistence: {
    entityName: 'Mktpshopeeattribute',
    idColumn: 'ATTRIBUTE_ID',
    valueColumn: 'DATAVALUE',
    unitColumn: 'UNITVALUE',
    keyColumns: ['CODECOMPANY', 'CODEPRODUCT', 'CODECONTRACT', 'CODESHOPEE']
  }
};

export const TIKTOK_ATTRIBUTE_PROFILE: AttributeProfile = {
  id: 'tiktok',
  labelKey: 'builderScreen.attributes.profile.tiktok',
  sourcePath: 'data.attributes',
  nesting: 'flat',
  fields: {
    id: 'id',
    label: 'name',
    required: 'is_required',
    options: 'values',
    optionValue: 'id',
    optionLabel: 'name',
    multiple: 'is_multiple_selection',
    allowFreeText: 'is_customizable'
  },
  persistence: {
    entityName: 'Mktptiktokattribute',
    idColumn: 'KEY',
    valueColumn: 'VALUE',
    optionsColumn: 'OPTIONS',
    keyColumns: ['CODECOMPANY', 'CODEPRODUCT', 'CODECONTRACT']
  }
};

export const B2W_ATTRIBUTE_PROFILE: AttributeProfile = {
  id: 'b2w',
  labelKey: 'builderScreen.attributes.profile.b2w',
  sourcePath: 'attributes',
  nesting: 'grouped',
  fields: {
    id: 'nameId',
    label: 'name',
    options: 'dadosValores',
    optionValue: 'id',
    optionLabel: 'name',
    group: 'grupId'
  },
  persistence: {
    entityName: 'Mktpskyhubattribute',
    idColumn: 'KEY_ESPECIFICATION',
    valueColumn: 'VALUE_ESPECIFICATION',
    keyColumns: ['CODECOMPANY', 'CODEPRODUCT', 'CODECONTRACT', 'CODESKYHUB']
  }
};

export const MELI_ATTRIBUTE_PROFILE: AttributeProfile = {
  id: 'meli',
  labelKey: 'builderScreen.attributes.profile.meli',
  sourcePath: '',
  nesting: 'grouped',
  fields: {
    id: 'id',
    label: 'name',
    options: 'values',
    optionValue: 'id',
    optionLabel: 'name',
    group: 'attribute_group_id',
    allowFreeText: 'value_type'
  },
  persistence: {
    entityName: 'Mktpmeliattribute',
    idColumn: 'ID_ATRIBUTO',
    valueColumn: 'VALUE',
    keyColumns: ['CODECOMPANY', 'CODEPRODUCT', 'CODECONTRACT']
  }
};

export const BUILTIN_ATTRIBUTE_PROFILES: readonly AttributeProfile[] = [
  SHOPEE_ATTRIBUTE_PROFILE,
  TIKTOK_ATTRIBUTE_PROFILE,
  B2W_ATTRIBUTE_PROFILE,
  MELI_ATTRIBUTE_PROFILE
];

/**
 * Lê um caminho com ponto e índice de array, tolerando ausência.
 * Ex.: 'response.list[0].attribute_tree'.
 */
export function readByPath(source: unknown, path: string): unknown {
  if (!path) {
    return source;
  }

  const segments = path
    .replace(/\[(\d+)\]/g, '.$1')
    .split('.')
    .filter(segment => segment.length > 0);

  let current: unknown = source;

  for (const segment of segments) {
    if (current === null || current === undefined) {
      return undefined;
    }
    if (Array.isArray(current)) {
      const index = Number(segment);
      if (!Number.isInteger(index)) {
        return undefined;
      }
      current = current[index];
      continue;
    }
    if (typeof current !== 'object') {
      return undefined;
    }
    current = (current as Record<string, unknown>)[segment];
  }

  return current;
}

/** Atributo já normalizado — a forma única que o renderer conhece. */
export interface NormalizedAttribute {
  readonly id: string;
  readonly label: string;
  readonly required: boolean;
  readonly multiple: boolean;
  readonly allowFreeText: boolean;
  readonly group?: string;
  readonly units?: readonly string[];
  readonly options: readonly NormalizedAttributeOption[];
}

export interface NormalizedAttributeOption {
  readonly value: string;
  readonly label: string;
  /** Atributos que só aparecem quando ESTA opção é escolhida. Shopee. */
  readonly children?: readonly NormalizedAttribute[];
}
